import { ChartFrame, LineLayer, ZeroLine } from '@finsim/charts';
import {
  addMonths,
  timeAxis,
  type MonthIndex,
  type Scheduled,
  type YearMonth,
} from '@finsim/model';
import { Plus, Scissors, Trash2 } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatMonthYear, formatYear } from '../../../shared/lib/format';
import { useLocale } from '../../../shared/lib/i18n/use-locale';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  IconButton,
  Tooltip,
  useReadOnly,
} from '../../../shared/ui';
import {
  addPeriod,
  fromPeriods,
  movePeriod,
  periodErrors,
  removePeriod,
  setPeriodValue,
  splitPeriod,
  toPeriods,
  type Period,
} from '../lib/schedule-edit';
import { valuePath } from '../lib/rate-preview';
import { KeyDateMonthField } from './key-date-month-field';

export interface ScheduleEditorProps<T> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  value: Scheduled<T>;
  onSave: (value: Scheduled<T>) => void;
  start: YearMonth;
  horizon: number;
  /** Editor for one period's value. */
  renderValue: (value: T, onChange: (value: T) => void, label: string) => ReactNode;
  /** Numeric value for the preview chart at month t. */
  toNumber: (value: T, t: number) => number;
  formatPreview: (value: number) => string;
}

/**
 * "Vary over time" dialog (CLAUDE.md "Time-varying values"): a table of periods (start + value),
 * add/split/remove actions, a preview of the resulting path and validation. Edits a draft; Save
 * commits once (one undo step). A single remaining period saves as a constant.
 */
export function ScheduleEditor<T>(props: ScheduleEditorProps<T>) {
  const { open, onOpenChange } = props;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open && <ScheduleEditorBody {...props} />}
    </Dialog>
  );
}

function ScheduleEditorBody<T>({
  onOpenChange,
  title,
  description,
  value,
  onSave,
  start,
  horizon,
  renderValue,
  toNumber,
  formatPreview,
}: ScheduleEditorProps<T>) {
  const { t } = useTranslation();
  const locale = useLocale();
  // Read-only (a shared project): the periods and preview, without editing actions.
  const readOnly = useReadOnly();
  const [periods, setPeriods] = useState<Period<T>[]>(() => toPeriods(value));
  const [confirmSingle, setConfirmSingle] = useState(false);
  const errors = periodErrors(periods, horizon);
  const invalid = errors.some((e) => e !== null);

  const dates = useMemo(() => timeAxis(start, horizon), [start, horizon]);
  const preview = useMemo(
    () => (invalid ? null : valuePath(fromPeriods(periods), horizon, toNumber)),
    [periods, horizon, toNumber, invalid],
  );
  const monthLabel = (from: number): string =>
    formatMonthYear(new Date(`${addMonths(start, from)}-01T00:00:00Z`), locale, 'long');

  const save = (next: readonly Period<T>[]): void => {
    onSave(fromPeriods(next));
    onOpenChange(false);
  };

  return (
    <DialogContent
      title={title}
      description={description ?? t('schedule.description')}
      closeLabel={t('common.close')}
      className="w-[min(640px,calc(100vw-32px))]"
      footer={
        readOnly ? (
          <DialogClose asChild>
            <Button variant="secondary">{t('common.close')}</Button>
          </DialogClose>
        ) : confirmSingle ? (
          <>
            <span className="mr-auto self-center text-xs text-text-2">
              {t('schedule.confirmSingle')}
            </span>
            <Button
              variant="ghost"
              onClick={() => {
                setConfirmSingle(false);
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => {
                save(periods.slice(0, 1));
              }}
            >
              {t('schedule.useSingle')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              className="mr-auto"
              onClick={() => {
                if (periods.length > 1) setConfirmSingle(true);
                else save(periods);
              }}
            >
              {t('schedule.useSingle')}
            </Button>
            <DialogClose asChild>
              <Button variant="ghost">{t('common.cancel')}</Button>
            </DialogClose>
            <Button
              variant="primary"
              disabled={invalid}
              onClick={() => {
                save(periods);
              }}
            >
              {t('schedule.save')}
            </Button>
          </>
        )
      }
    >
      <table className="w-full text-sm">
        <caption className="sr-only">{title}</caption>
        <thead>
          <tr className="text-xs text-text-3">
            <th scope="col" className="w-48 pb-1.5 text-left font-normal">
              {t('schedule.from')}
            </th>
            <th scope="col" className="pb-1.5 text-left font-normal">
              {t('schedule.value')}
            </th>
            {!readOnly && (
              <th scope="col" className="w-16 pb-1.5">
                <span className="sr-only">{t('schedule.actions')}</span>
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {periods.map((period, index) => {
            const label = t('schedule.period', { n: index + 1 });
            const error = errors[index];
            return (
              <tr key={index} className="align-top">
                <td className="py-1 pr-2">
                  {index === 0 ? (
                    <p className="flex h-7 items-center text-text-2">
                      {t('schedule.fromStart', { date: monthLabel(0) })}
                    </p>
                  ) : (
                    <KeyDateMonthField
                      aria-label={`${label} — ${t('schedule.from')}`}
                      value={period.from as MonthIndex}
                      anchor={period.anchor}
                      start={start}
                      min={1}
                      max={horizon - 1}
                      onCommit={(from, anchor) => {
                        setPeriods((p) => movePeriod(p, index, from, anchor));
                      }}
                    />
                  )}
                  {error && (
                    <p className="mt-1 text-xs text-negative">{t(`schedule.errors.${error}`)}</p>
                  )}
                </td>
                <td className="py-1 pr-2">
                  {renderValue(
                    period.value,
                    (v) => {
                      setPeriods((p) => setPeriodValue(p, index, v));
                    },
                    label,
                  )}
                </td>
                {!readOnly && (
                  <td className="py-1">
                    <div className="flex justify-end">
                      <Tooltip content={t('schedule.split')}>
                        <IconButton
                          label={`${t('schedule.split')} — ${label}`}
                          onClick={() => {
                            setPeriods((p) => splitPeriod(p, index, horizon));
                          }}
                        >
                          <Scissors />
                        </IconButton>
                      </Tooltip>
                      {index > 0 && (
                        <Tooltip content={t('schedule.remove')}>
                          <IconButton
                            label={`${t('schedule.remove')} — ${label}`}
                            onClick={() => {
                              setPeriods((p) => removePeriod(p, index));
                            }}
                          >
                            <Trash2 />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
      {!readOnly && (
        <Button
          size="sm"
          variant="ghost"
          className="mt-1"
          leadingIcon={<Plus />}
          onClick={() => {
            setPeriods((p) => addPeriod(p, horizon));
          }}
        >
          {t('schedule.add')}
        </Button>
      )}

      {preview && (
        <div className="mt-3 rounded-md border border-border-subtle bg-bg-1 px-2 pt-2">
          <p className="px-1 text-xs text-text-3">{t('schedule.preview')}</p>
          <ChartFrame
            dates={dates}
            height={130}
            domainSeries={[preview]}
            formatY={formatPreview}
            formatXTick={(d) => formatYear(d, locale)}
            margin={{ top: 8, right: 8, bottom: 22 }}
            ariaLabel={t('schedule.previewAria', { title })}
            events={periods
              .slice(1)
              .map((p, i) => ({ index: p.from, label: t('schedule.period', { n: i + 2 }) }))}
            renderTooltip={(i) => (
              <p className="flex justify-between gap-3">
                <span className="text-text-3">
                  {dates[i] ? formatMonthYear(dates[i], locale) : ''}
                </span>
                <span className="tabular font-medium">{formatPreview(preview[i] ?? 0)}</span>
              </p>
            )}
          >
            <ZeroLine />
            <LineLayer values={preview} color="var(--chart-series-1)" />
          </ChartFrame>
        </div>
      )}
    </DialogContent>
  );
}
